import { useState } from 'react';
import { TriangleAlert, Undo2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { PCMP_ACCOUNTS_ACTION_COPY } from '../../constants/pcmpAccounts.constants';
import type { PcmpFailureStage } from '../../types/pcmpProposal.types';
import { PcmpReturnPaymentDialog } from '../PcmpReturn/PcmpReturnPaymentDialog';
import { PcmpAccountsFailDialog } from './PcmpAccountsFailDialog';

type OpenDialog = 'FAIL' | 'RETURN';

interface PcmpAccountsActionButtonsProps {
  proposalId: number;
  refNo: string;
  /** Offer Disbursement Failed, at this stage; null hides it. */
  fail: { stage: PcmpFailureStage | null } | null;
  /** Admin / MIS may return the payout, all or part (LDS-765). */
  canReturn: boolean;
  className?: string;
}

/**
 * Disbursement Failed and Return for one Accounts payout, each opening its
 * dialog. Whether they show is the caller's call.
 */
export function PcmpAccountsActionButtons({
  proposalId,
  refNo,
  fail,
  canReturn,
  className,
}: PcmpAccountsActionButtonsProps) {
  const [open, setOpen] = useState<OpenDialog | null>(null);
  const close = () => setOpen(null);

  return (
    <>
      {fail || canReturn ? (
        <div className={cn('flex flex-wrap items-center gap-2', className)}>
          {fail ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setOpen('FAIL')}
              className="gap-1.5 text-destructive hover:text-destructive"
            >
              <TriangleAlert className="size-3.5" />
              {PCMP_ACCOUNTS_ACTION_COPY.FAIL.label}
            </Button>
          ) : null}
          {canReturn ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setOpen('RETURN')}
              className="gap-1.5"
            >
              <Undo2 className="size-3.5" />
              {PCMP_ACCOUNTS_ACTION_COPY.RETURN.label}
            </Button>
          ) : null}
        </div>
      ) : null}

      {/* Mounted only while open, so each starts empty; they outlive the buttons, so a refused action stays readable. */}
      {open === 'FAIL' ? (
        <PcmpAccountsFailDialog
          target={{ proposalId, refNo, failureStage: fail?.stage }}
          onClose={close}
        />
      ) : null}
      {open === 'RETURN' ? (
        <PcmpReturnPaymentDialog
          target={{ proposalId, refNo, channel: 'ACCOUNTS' }}
          onClose={close}
        />
      ) : null}
    </>
  );
}
